<template>
  <div class="flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--titlegroup">
        <span class="a-header--title slide-down-enter-active">Grouped Alerts</span>
        <HelpButton :content="HELP_CONTENT.changeEvents" />
      </span>
    </div>
    <div v-if="isLoading" class="d-flex justify-content-center align-items-center pt-35-px">
      <Spinner />
    </div>
    <transition name="slide-up">
      <div v-if="!isLoading" class="a-filter">
        <a-row class="full-w" type="flex" justify="start" :gutter="24">
          <a-col :span="8">
            <div class="full-w a-input-group" :class="{ active: !!filterParams.search }">
              <a-button size="large">
                <a-icon type="search" />
              </a-button>
              <a-input class="custom-prefix" placeholder="Search by website" :default-value="filterParams.search" @change="onSearch">
                <a-icon v-show="isSearching" slot="suffix" class="custom-suffix" type="loading" spin />
              </a-input>
            </div>
          </a-col>
          <a-col :span="8">
            <div class="full-w a-input-group" :class="{ active: !!filterParams.from && !!filterParams.to }">
              <a-button size="large">
                <a-icon type="filter" />
              </a-button>
              <a-range-picker
                :ranges="RANGE"
                :disabledDate="disabledDate"
                :placeholder="['From', 'To']"
                :format="DATE_FORMAT.DDMMMYYYY"
                @change="onChangeDate"
              />
            </div>
          </a-col>
          <a-col :span="8">
            <div class="full-w a-select-group" :class="{ active: hasCombinedFilterActive }">
              <a-button size="large">
                <a-icon type="filter" />
              </a-button>
              <a-select
                v-model="filterParams.combinedFilter"
                mode="multiple"
                :size="'large'"
                placeholder="Filter by severity, category, change type..."
                :maxTagCount="2"
                :maxTagTextLength="12"
                optionFilterProp="children"
                @change="onChangeCombinedFilter"
              >
                <a-select-opt-group label="Severity">
                  <a-select-option v-for="option in SEVERITY_OPTIONS" :key="'severity:' + option.value" :value="'severity:' + option.value">
                    {{ option.label }}
                  </a-select-option>
                </a-select-opt-group>
                <a-select-opt-group label="Category">
                  <a-select-option v-for="option in CATEGORY_OPTIONS" :key="'impact:' + option.value" :value="'impact:' + option.value">
                    {{ option.label }}
                  </a-select-option>
                </a-select-opt-group>
                <a-select-opt-group label="Change Type">
                  <a-select-option v-for="option in CHANGE_TYPE_OPTIONS" :key="'class:' + option.value" :value="'class:' + option.value">
                    {{ option.label }}
                  </a-select-option>
                </a-select-opt-group>
              </a-select>
            </div>
          </a-col>
        </a-row>
        <a-row class="full-w mt-2" type="flex" justify="start" :gutter="24">
          <a-col :span="8">
            <div class="full-w a-select-group" :class="{ active: !!filterParams.status }">
              <a-button size="large">
                <a-icon type="flag" />
              </a-button>
              <a-select v-model="filterParams.status" :size="'large'" placeholder="Any status" allowClear @change="onChangeStatus">
                <a-select-option v-for="option in STATUS_OPTIONS" :key="option.value" :value="option.value">
                  {{ option.label }}
                </a-select-option>
              </a-select>
            </div>
          </a-col>
          <a-col :span="8">
            <!-- Sorting is done by the API. The table's own header sort would
                 only reorder the rows on this page. -->
            <div class="full-w a-select-group active">
              <a-button size="large">
                <a-icon type="sort-ascending" />
              </a-button>
              <a-select v-model="filterParams.sort" :size="'large'" @change="onChangeSort">
                <a-select-option v-for="option in SORT_OPTIONS" :key="option.value" :value="option.value">
                  Sort: {{ option.label }}
                </a-select-option>
              </a-select>
            </div>
          </a-col>
        </a-row>
      </div>
    </transition>
    <div v-resize="elSizeChange" class="a-body flex-grow-1 overflow-y-hidden">
      <ConfigBanner v-if="!isLoading" mode="grouping" :config="config" />
      <transition name="slide-up">
        <template v-if="!isLoading">
          <PaginationTable :clientPaging="false" :pagination="pagin" :columns="ChangeEventListColumn" :dataSource="filterData" :tableSize="tableSize">
            <template slot="groupId" slot-scope="{ record }">
              <router-link :to="'/change-events/detail/' + record.id">#{{ record.id }}</router-link>
            </template>
            <template slot="categoryTitle">
              <HeaderHelp label="Category" :items="categoryHelp" />
            </template>
            <template slot="changeTypeTitle">
              <HeaderHelp
                label="Change Type"
                lead="A group covers one website only."
                :items="changeTypeHelp"
              />
            </template>
            <!-- The website opens the aggregated alert. Its summary is the
                 tooltip, so the row still says what happened without a column
                 that repeats the Category beside it. -->
            <template slot="site" slot-scope="{ record }">
              <div>
                <a-tooltip :title="record.summary || record.site" placement="topLeft">
                  <router-link class="ce-site" :to="'/change-events/detail/' + record.id">
                    <a-icon class="ce-site__icon" type="link" />{{ record.site }}
                  </router-link>
                </a-tooltip>
                <p v-if="record.hasMalware" class="mb-0">
                  <a-tag color="red">Malware found</a-tag>
                </p>
              </div>
            </template>
            <template slot="severity" slot-scope="{ record }">
              <SeverityMix compact :severity="record.severity" :mix="record.severityMix" />
            </template>
            <template slot="firstSeen" slot-scope="{ record }">
              <div>
                <p class="mb-0">
                  <time>{{ record.firstSeen | dateString }}</time>
                  <br />
                  <time>{{ record.firstSeen | timeString }}</time>
                </p>
              </div>
            </template>
            <template slot="lastSeen" slot-scope="{ record }">
              <div>
                <p class="mb-0">
                  <time>{{ record.lastSeen | dateString }}</time>
                  <br />
                  <time>{{ record.lastSeen | timeString }}</time>
                </p>
              </div>
            </template>
            <template slot="status" slot-scope="{ record }">
              <a-tag :color="record.status === 'open' ? 'blue' : ''">
                {{ record.statusLabel }}
              </a-tag>
            </template>
            <template slot="operation" slot-scope="{ record }">
              <a-dropdown placement="topLeft">
                <a-icon class="operation" type="ellipsis" />
                <a-menu slot="overlay" class="operation-menu">
                  <a-menu-item>
                    <a-icon type="eye" />
                    <router-link class="nav-link-detail" :to="'/change-events/detail/' + record.id"> View Detail </router-link>
                  </a-menu-item>
                </a-menu>
              </a-dropdown>
            </template>
          </PaginationTable>
        </template>
      </transition>
    </div>
    <div class="a-footer">
      <PaginationBar :pageSize.sync="pagin.pageSize" :total="pagin.total" @change="paginChange" />
    </div>
  </div>
</template>

<script>
  import { debounce } from 'lodash';
  import { PAGE_OPTIONS } from '@constants/pageOptions';
  import { DATE_FORMAT } from '@constants/dateFormat';
  import { indexItemTable } from '@shared/helpers/utils';
  import { ChangeEventListColumn } from '../columnsTable/ChangeEventListColumn';
  import ChangeEventsService from '../services/ChangeEvents.service';
  import {
    SEVERITY_OPTIONS,
    CATEGORY_OPTIONS,
    CHANGE_TYPE_OPTIONS,
    STATUS_OPTIONS,
    SORT_OPTIONS,
    DEFAULT_SORT,
    STATUS_LABELS,
    labelFor,
    categoryLabel,
    changeTypeLabel,
    severityMix,
    categoryHelpItems,
    changeTypeHelpItems
  } from '../consts/ChangeEvents.consts';
  import utcDateRange from '../mixins/utcDateRange.mixin';
  import { HELP_CONTENT } from '../consts/helpContent';
  import HelpButton from './HelpButton.vue';
  import SeverityMix from './SeverityMix.vue';
  import ConfigBanner from './ConfigBanner.vue';
  import HeaderHelp from './HeaderHelp.vue';

  export default {
    name: 'ChangeEventList',
    components: { HelpButton, SeverityMix, ConfigBanner, HeaderHelp },
    mixins: [utcDateRange],
    data() {
      let pageSize = Number(window.localStorage.getItem('pageSize'));
      if (!pageSize) pageSize = 5;

      return {
        HELP_CONTENT,
        PAGE_OPTIONS,
        DATE_FORMAT,
        ChangeEventListColumn,
        SEVERITY_OPTIONS,
        CATEGORY_OPTIONS,
        CHANGE_TYPE_OPTIONS,
        STATUS_OPTIONS,
        SORT_OPTIONS,
        // The rows on screen. The API pages, sorts and filters them, so there
        // is no second client-side copy to keep in step.
        filterData: [],
        // The rule these alerts were grouped by, shown above the list.
        config: null,
        filterParams: {
          search: '',
          // UTC strings, ready to send.
          from: null,
          to: null,
          // 'severity:X' / 'impact:X' / 'class:X' — must match the option values.
          combinedFilter: this.filterFromQuery(),
          status: undefined,
          sort: DEFAULT_SORT
        },
        pagin: {
          current: 1,
          pageSize: pageSize,
          total: 0
        },
        isSearching: false,
        isLoading: true,
        tableSize: {
          height: '',
          width: ''
        }
      };
    },
    computed: {
      /** What the Category heading explains on hover. */
      categoryHelp() {
        return categoryHelpItems('grouping');
      },
      /** What the Change Type heading explains on hover. */
      changeTypeHelp() {
        return changeTypeHelpItems();
      },
      hasCombinedFilterActive() {
        return Array.isArray(this.filterParams.combinedFilter) && this.filterParams.combinedFilter.length > 0;
      },
      /** Splits the one multi-select back into the three API parameters. */
      parsedCombinedFilter() {
        const result = { severity: [], impactType: [], eventClass: [] };
        (this.filterParams.combinedFilter || []).forEach((val) => {
          if (val.startsWith('severity:')) {
            result.severity.push(val.replace('severity:', ''));
          } else if (val.startsWith('impact:')) {
            result.impactType.push(val.replace('impact:', ''));
          } else if (val.startsWith('class:')) {
            result.eventClass.push(val.replace('class:', ''));
          }
        });
        return result;
      }
    },
    mounted() {
      this.fetchList();
      this.loadConfig();
    },
    methods: {
      /**
       * The settings these alerts were grouped by.
       *
       * Quiet on failure: the list is still correct without the band.
       */
      async loadConfig() {
        try {
          this.config = await ChangeEventsService.fetchConfig();
        } catch (err) {
          this.config = null;
        }
      },
      /**
       * Honours `?severity=` and `?impact=` so the Overview can deep-link here.
       * @returns {string[]}
       */
      filterFromQuery() {
        const query = (this.$route && this.$route.query) || {};
        const picked = [];
        const severity = Array.isArray(query.severity) ? query.severity[0] : query.severity;
        if (SEVERITY_OPTIONS.some((o) => o.value === severity)) picked.push('severity:' + severity);
        const impact = Array.isArray(query.impact) ? query.impact[0] : query.impact;
        if (CATEGORY_OPTIONS.some((o) => o.value === impact)) picked.push('impact:' + impact);
        return picked;
      },
      onSearch: debounce(function (inputChangeEvent) {
        const inputSearchValue = inputChangeEvent.target.value;
        this.filterParams.search = inputSearchValue ? String(inputSearchValue).trim() : '';
        this.pagin.current = 1;
        this.fetchList();
      }, 800),
      async fetchList() {
        try {
          this.isSearching = true;
          const { severity, impactType, eventClass } = this.parsedCombinedFilter;
          const responseData = await ChangeEventsService.fetchList({
            page: this.pagin.current,
            perPage: this.pagin.pageSize,
            search: this.filterParams.search,
            from: this.filterParams.from,
            to: this.filterParams.to,
            severity,
            impactType,
            eventClass,
            status: this.filterParams.status,
            sort: this.filterParams.sort
          });
          const rows = (responseData && responseData.data) || [];
          this.filterData = rows.map((item, index) => ({
            index: indexItemTable(this.pagin.current, this.pagin.pageSize, index),
            id: item.id,
            site: item.site || '',
            summary: item.summary || '',
            categoryLabel: categoryLabel(item),
            changeTypeLabel: changeTypeLabel(item),
            severity: item.severity || 'unscored',
            // `null` when the API has not recounted this row yet. The severity
            // cell then shows the worst severity on its own.
            severityMix: severityMix(item),
            alertCount: item.alert_count == null ? 0 : item.alert_count,
            pageCount: item.page_count == null ? 0 : item.page_count,
            firstSeen: item.first_seen,
            lastSeen: item.last_seen,
            status: item.status,
            statusLabel: labelFor(STATUS_LABELS, item.status),
            hasMalware: Boolean(item.has_malware)
          }));
          this.pagin.total = (responseData && responseData.total) || 0;
          this.isSearching = false;
        } catch (err) {
          this.isSearching = false;
          this.$showMultiErrorMessage(err.messages);
        } finally {
          if (this.isLoading) {
            setTimeout(() => {
              this.isLoading = false;
            }, 300);
          }
        }
      },
      onChangeCombinedFilter(selectedValues) {
        this.filterParams.combinedFilter = Array.isArray(selectedValues) ? selectedValues : [];
        this.pagin.current = 1;
        this.fetchList();
      },
      onChangeStatus(value) {
        this.filterParams.status = value;
        this.pagin.current = 1;
        this.fetchList();
      },
      onChangeSort(value) {
        this.filterParams.sort = value;
        this.pagin.current = 1;
        this.fetchList();
      },
      onChangeDate(dates, dateStrings) {
        this.filterParams.from = dateStrings[0] ? this.getFilteredUTCStartDate(dateStrings[0], 'DD MMM YYYY') : null;
        this.filterParams.to = dateStrings[1] ? this.getFilteredUTCEndDate(dateStrings[1], 'DD MMM YYYY') : null;
        this.pagin.current = 1;
        this.fetchList();
      },
      paginChange(pagin) {
        this.pagin = { ...pagin };
        this.fetchList();
      },
      elSizeChange(size) {
        this.tableSize = size;
      }
    }
  };
</script>

<style lang="scss" scoped>
  .nav-link-detail {
    display: inline-block;
    width: 100%;
    &:hover {
      text-decoration: underline;
    }
  }
  .custom-suffix {
    font-size: 20px;
    color: #20a4b8;
  }
  // The website name opens the aggregated alert. It used to inherit the body
  // colour, so it read as plain text and nobody knew it could be clicked.
  .ce-site {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #20a4b8;
    font-weight: 600;

    &__icon {
      margin-right: 5px;
      font-size: 11px;
      opacity: 0.75;
    }
    &:hover {
      color: #17808f;
      text-decoration: underline;
    }
  }
</style>
